/**
 * The Tagging preview's own controls, in place of the browser's: Play and Stop latch (Play: every row
 * plays as it is selected; Stop: a row only shows its first frame), Pause pauses and carries on, the
 * speaker mutes, the bar scrubs and the readout gives the playhead against the clip's length. They
 * follow the video's own events, so they always match what the picture is doing, and report the playhead
 * up so the rows beneath can light as it passes them. The transport sits centred under the picture.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { Speaker } from '@/components/speaker';
import { Tool, Toolbar } from '@/components/toolbar';
import { fmtTime } from '@/lib/format';
import type { PreviewMode } from '@/lib/preview-mode';

export function PreviewControls({ videoRef, durationMs, mode, onPlay, onPause, onStop, onClock }: {
  videoRef: RefObject<HTMLVideoElement | null>;
  durationMs: number;
  mode: PreviewMode;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  /** Where the playhead is, and whether the picture is moving. */
  onClock: (atMs: number, playing: boolean) => void;
}) {
  const clock = useRef(onClock);
  clock.current = onClock;
  const [atMs, setAtMs] = useState(0);
  const [muted, setMuted] = useState(false);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      const ms = Math.round(v.currentTime * 1000);
      setAtMs(ms);
      clock.current(ms, !v.paused);
    };
    const onVolume = () => setMuted(v.muted);
    const timeEvents = ['timeupdate', 'seeked', 'play', 'pause', 'loadedmetadata'] as const;
    for (const e of timeEvents) v.addEventListener(e, onTime);
    v.addEventListener('volumechange', onVolume);
    onTime();
    onVolume();
    return () => {
      for (const e of timeEvents) v.removeEventListener(e, onTime);
      v.removeEventListener('volumechange', onVolume);
    };
  }, [videoRef]);

  return (
    <div className="preview-controls">
      <div className="preview-bar">
        <div className="row preview-transport">
          <Toolbar dense>
            <Tool icon="play" title="Play: every row plays as you select it" active={mode === 'play'} onClick={onPlay} />
            <Tool icon="pause" title="Pause, or carry on" onClick={onPause} />
            <Tool icon="stop" title="Stop: rows hold on their first frame and never play" active={mode === 'stop'} onClick={onStop} />
          </Toolbar>
          <Speaker muted={muted} label="the preview" onToggle={() => { const v = videoRef.current; if (v) v.muted = !v.muted; }} />
        </div>
        <span className="mono sub preview-time">{fmtTime(atMs, false)} / {fmtTime(durationMs, false)}</span>
      </div>
      <input type="range" className="preview-scrub" min={0} max={durationMs} step={1} value={Math.min(atMs, durationMs)} aria-label="Playhead"
        onChange={(e) => { const v = videoRef.current; if (v) v.currentTime = Number(e.target.value) / 1000; }} />
    </div>
  );
}
